<script lang="ts">
  import type { Player } from '../models/player.type';
  import { randomFloat } from '../utils/math';
  import { StatusEffect } from '../models/status-effects.type';

  interface Props {
    data: Player;
    reverse?: boolean;
  }

  let { data, reverse = false }: Props = $props();

  // Fixed per bar, so each shimmers out of step with the others.
  const delay = {
    health: randomFloat(-10, 0),
    poison: randomFloat(-10, 0),
    freezing: randomFloat(-10, 0),
    block: randomFloat(-10, 0),
    barrier: randomFloat(-10, 0),
    regen: randomFloat(-10, 0),
  };

  // The player is mutated in place and handed back as the same object, so these
  // recompute because the parent re-sets `data`, not because a field changed.
  function bar(type: StatusEffect, key: keyof typeof delay) {
    const value = data.statusEffects?.find((mod) => mod.type === type)?.stacks || 0;
    return { value, percent: (value / data.healthMax) * 100, delay: delay[key] };
  }

  let health = $derived({
    max: data.healthMax,
    value: data.healthCurrent,
    percent: (data.healthCurrent / data.healthMax) * 100,
    delay: delay.health,
  });
  let poison = $derived({
    ...bar(StatusEffect.Poison, 'poison'),
    top: ((data.healthMax - data.healthCurrent) / data.healthMax) * 100,
  });
  let freezing = $derived(bar(StatusEffect.Freezing, 'freezing'));
  let block = $derived(bar(StatusEffect.Block, 'block'));
  let barrier = $derived(bar(StatusEffect.Barrier, 'barrier'));
  let regen = $derived(bar(StatusEffect.Regen, 'regen'));
</script>

<!-- <Chrome edge='square' thickness='thin' gloss={false} chroming='obsidian' hover={false}> -->
<div class="health-container {reverse ? 'reverse' : ''}">
  <div class="container">
    <!-- {#each notches as notch, index}
          <div class='notch {notch}'></div>
      {/each} -->
    <div class="bar empty" style="height: 100%;">
      <div
        class="bar subbar health"
        style="--delay: {health.delay}s; height: {health.percent}%;"
      ></div>
      <div
        class="bar subbar poisoned"
        style="--delay: {poison.delay}s; height: {poison.percent}%; top: {poison.top}%"
      ></div>
      <div
        class="bar subbar frozen"
        style="--delay: {freezing.delay}s; height: {freezing.percent}%;"
      ></div>

      <div
        class="bar subbar regen"
        style="--delay: {regen.delay}s; height: {regen.percent}%; bottom: {health.percent}%"
      ></div>

      <div
        class="bar subbar block"
        style="--delay: {block.delay}s; height: {block.percent}%; bottom: {barrier.percent}%"
      ></div>
      <div
        class="bar subbar barrier"
        style="--delay: {barrier.delay}s; height: {barrier.percent}%;"
      ></div>
    </div>

    <div class="notches">
      {#each { length: health.max + 1 } as _, i}
        <div class="notch"></div>
      {/each}
    </div>

    <!-- <div class='hover-container'>
        <div class='level level1'>
          {#each level1 as level, index}
            <div class='{level.title}' style='flex-grow: {level.value};'>

              <div class='level level2'>
                {#each level.level2 as level2, index}
                  {#if level2.value}
                    <div class='{level2.title}' style='flex-grow: {level2.value};'>
                      {level2.value} {level2.title}
                    </div>
                  {/if}
                {/each}
              </div>
            </div>
          {/each}
        </div>
      </div> -->
  </div>
</div>

<!-- </Chrome> -->

<style lang="less">
  @import '../../defines.less';

  .health-container {
    --health: @health;
    --empty: @empty;
    --freezing: @freezing;
    --poison: @poison;
    --block: @block;
    --barrier: @barrier;
    --regeneration: @regeneration;

    display: flex;
    justify-content: end;
    flex-direction: column-reverse;
    grid-gap: 0px;
    transition: grid-gap 0.3s;
    position: relative;
    width: calc(var(--health-bar-width) - 4px);
    height: calc(100% - 4px);
    background: rgba(0, 0, 0, 0.5);
    border: 2px solid black;

    /* &:hover{
      grid-gap: 10px;

      .container{
        .hover-container{
          pointer-events: all;
          opacity: 1;
          left: var(--health-bar-width);
        }
      }
      &.reverse{
        .container{
          .hover-container{
            right: var(--health-bar-width);
          }
        }
      }
    } */

    .container {
      display: flex;
      justify-content: start;
      flex-direction: column-reverse;
      position: relative;
      font-family: 'Roboto Condensed';
      height: 100%;
      width: 100%;
      overflow: hidden;

      .bar {
        position: relative;
        width: 100%;
        transition:
          height 0.3s ease-out,
          top 0.3s ease-out,
          bottom 0.3s ease-out;
        display: flex;
        flex-direction: column-reverse;

        &::before,
        &::after {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          pointer-events: none;
          opacity: 0.5;
          background-image: url('../../assets/effects/health-bar.jpg');
          // background-image: url('../../assets/dice/test (1).jpg');
          background-position: center;
          mix-blend-mode: color-burn;
          filter: blur(5px);
          animation: scroll-bg-right 10s linear infinite;
          animation-delay: var(--delay);
        }
        &::after {
          animation: scroll-bg-left 10s linear infinite;
          animation-delay: var(--delay);
        }

        @keyframes scroll-bg-left {
          0% {
            background-position-x: 0;
          }
          100% {
            background-position-x: 1024px;
          }
        }
        @keyframes scroll-bg-right {
          0% {
            background-position-x: 1024px;
          }
          100% {
            background-position-x: 0;
          }
        }

        .subbar {
          position: absolute;
        }

        &.health {
          // background-color: var(--health);
          background-image: linear-gradient(
            to right,
            darken(@health, 30%) 0%,
            @health 20%,
            lighten(@health, 20%) 30%,
            @health 50%,
            @health 80%,
            darken(@health, 30%)
          );
          position: absolute;
          bottom: 0;
        }
        &.empty {
          background-image: linear-gradient(
            to right,
            darken(@empty, 30%) 0%,
            @empty 20%,
            lighten(@empty, 40%) 30%,
            @empty 50%,
            @empty 80%,
            darken(@empty, 30%)
          );

          &::before,
          &::after {
            content: unset;
          }
        }

        &.block {
          background-image: linear-gradient(
            to right,
            transparent 30%,
            @block 70%,
            darken(@block, 30%)
          );
        }
        &.barrier {
          background-image: linear-gradient(
            to right,
            transparent 30%,
            @barrier 70%,
            darken(@barrier, 30%)
          );
        }

        &.poisoned {
          background-image: linear-gradient(
            to right,
            darken(@poison, 30%) 0%,
            @poison 20%,
            lighten(@poison, 20%) 30%,
            @poison 50%,
            @poison 80%,
            darken(@poison, 30%)
          );
          top: 0;
        }
        &.frozen {
          background-image: linear-gradient(
            to right,
            darken(@freezing, 30%) 0%,
            @freezing 20%,
            lighten(@freezing, 20%) 30%,
            @freezing 50%,
            @freezing 80%,
            darken(@freezing, 30%)
          );
        }

        &.regen {
          background-image: linear-gradient(
            to right,
            darken(@regeneration, 30%) 0%,
            @regeneration 20%,
            lighten(@regeneration, 20%) 30%,
            @regeneration 50%,
            transparent 90%
          );
        }
      }

      .notches {
        position: absolute;
        top: 0;
        left: 0;
        bottom: 0;
        right: 0;
        height: 100%;
        width: 100%;
        display: flex;
        flex-direction: column;
        justify-content: space-between;

        .notch {
          width: 75%;
          margin: 0 auto;
          height: 1px;
          background: linear-gradient(to left, transparent, white, transparent);
          // backdrop-filter: brightness(.8);
        }

        .notch:first-child,
        .notch:last-child {
          background: none;
        }
      }

      /* .hover-container{
        position: absolute;
        opacity: 0;
        transition: left 0.3s, right 0.3s, opacity 0.3s;
        z-index: 111;
        height: 100%;
        left: 0px;
        display: flex;
        pointer-events: none;

        .level{
          display: flex;
          flex-direction: column-reverse;

          &.level1{
            position: relative;
            width: 20px;
          }

          &.level2{
            position: absolute;
            top: 0;
            bottom: 0;
            left: 20px;
            right: -100px;
            width: 100px;

            div{
              display: flex;
              align-items: center;
              justify-content: end;
              padding-right: 5px;
            }
          }
          div{
            background: linear-gradient(to right, var(--color1,transparent) 0%, var(--color2,transparent) 20%);
          }

          .health{
            background-color: var(--health);
            position: relative;
            --color1: var(--health);
          }
          .empty{
            background-color: var(--empty);
            position: relative;
            --color1: var(--empty);
          }
          .over{
            position: relative;
          }

          .block{
            background-color: var(--block);
            color: black;
          }
          .poison{
            background-color: var(--poison);
          }
          .armoured{
            background-color: black;
          }
          .barrier{
            background-color: var(--barrier);
            color: black;
          }
          .regen{
            background-color: var(--regeneration);
          }
          .freezing{
            background-color: var(--freezing);
            // color: black;
          }
        }
      } */
    }

    &.reverse {
      justify-content: start;
      flex-direction: column;

      .notch {
        background: linear-gradient(210deg, var(--color1, pink) 40%, var(--color2, pink) 60%);
      }

      .container {
        display: flex;
        justify-content: start;
        flex-direction: column;

        /* .hover-container{
          left: unset;
          right: 0px;

          .level{
            flex-direction: column;

            .level2{
              right: 20px;
              left: -100px;

              div{
                justify-content: start;
                padding-left: 5px;
                background-image: linear-gradient(to left, var(--color1,transparent) 0%, var(--color2,transparent) 20%);
              }
            }
          }
        } */
      }
    }

    /* .notch{
      height: 4px;
      border-radius: 4px;
      margin: 2px;
      background: linear-gradient(30deg, var(--color1,pink) 40%, var(--color2,pink) 60%);
      transition: background 0.5s;

      &.health{
        --color1: var(--health);
        --color2: var(--health);
      }
      &.empty{
        --color1: var(--empty);
        --color2: var(--empty);
      }
      &.block{
        --color1: black;
        --color2: black;
      }

      &.poison{
        --color1: var(--poison);
        // --color1: #5e0000;
      }
      &.armoured{
        --color1: var(--empty);
        --color2: var(--block);
      }
      &.barrier{
        --color1: var(--barrier);
        --color2: var(--barrier);
      }
      &.regen{
        --color2: var(--regeneration);
      }
      &.freezing{
        --color1: var(--freezing);
      }
    } */

    /* .hover{
      position: absolute;
      background: black;
      z-index: 1111;
    } */
  }
</style>
